/**
 * Interactive star rating component
 */

import React, { useState } from 'react';
import styled from 'styled-components';

const RatingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const Star = styled.button<{ $filled: boolean; $hovered: boolean }>`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => 
    props.$filled || props.$hovered ? '#ffc107' : '#e1e5e9'
  };
  transition: color 0.2s;
  padding: 0.25rem;
  line-height: 1;

  &:hover {
    color: #ffc107;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const RatingText = styled.span`
  margin-left: 0.5rem;
  font-weight: 500;
  color: #333;
`;

interface StarRatingProps {
  rating: number;
  onRatingChange?: (rating: number) => void;
  maxRating?: number;
  readonly?: boolean;
  size?: 'small' | 'medium' | 'large';
  showText?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  onRatingChange,
  maxRating = 5,
  readonly = false,
  size = 'medium',
  showText = true
}) => {
  const [hoveredRating, setHoveredRating] = useState(0);

  const getSizeStyle = () => {
    switch (size) {
      case 'small':
        return { fontSize: '1rem' };
      case 'large':
        return { fontSize: '2rem' };
      default:
        return { fontSize: '1.5rem' };
    }
  };

  const handleStarClick = (starRating: number) => {
    if (readonly || !onRatingChange) return;
    onRatingChange(starRating);
  };

  const handleStarHover = (starRating: number) => {
    if (readonly) return;
    setHoveredRating(starRating);
  };

  const handleMouseLeave = () => {
    setHoveredRating(0);
  };

  return (
    <RatingContainer onMouseLeave={handleMouseLeave}>
      {Array.from({ length: maxRating }, (_, index) => {
        const starRating = index + 1;
        const isFilled = starRating <= rating;
        const isHovered = hoveredRating > 0 && starRating <= hoveredRating;

        return (
          <Star
            key={starRating}
            $filled={isFilled}
            $hovered={isHovered}
            disabled={readonly}
            onClick={() => handleStarClick(starRating)}
            onMouseEnter={() => handleStarHover(starRating)}
            style={getSizeStyle()}
            type="button"
          >
            {isFilled || isHovered ? '★' : '☆'}
          </Star>
        );
      })}
      {showText && <RatingText>{rating}/{maxRating}</RatingText>}
    </RatingContainer>
  );
};

export default StarRating;